import React, { useState } from 'react';
import { ArrowRight, TrendingUp, Shield, Target, Mail, Phone, MapPin } from 'lucide-react';
import axios from 'axios';
import '../styles/investment.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error message when user starts typing
    if (errorMessage) setErrorMessage('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage('');
    
    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.data.success) {
        console.log('Contact inquiry submitted successfully:', response.data.inquiry_id);
        setFormSubmitted(true);
        setTimeout(() => {
          setFormSubmitted(false);
          setFormData({ name: '', email: '', phone: '', message: '' });
        }, 4000);
      }
    } catch (error) {
      console.error('Error submitting contact form:', error);
      setErrorMessage(
        error.response?.data?.detail || 
        'An error occurred while submitting your inquiry. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="investment-page">
      {/* Header */}
      <header className="investment-header">
        <div className="header-content">
          <div className="logo-container">
            <img 
              src="https://customer-assets.emergentagent.com/job_146d9f7f-91a9-44b9-b93f-54116f665c02/artifacts/l80688sx_337F2E5A-C7A4-47BA-A13A-887FE2B999C7.png" 
              alt="Teranga Capital Investments" 
              className="logo"
            />
          </div>
          <nav className="nav-menu">
            <a href="#home" className="nav-link">Home</a>
            <a href="#about" className="nav-link">About</a>
            <a href="#performance" className="nav-link">Performance</a>
            <a href="#focus" className="nav-link">Investment Focus</a>
            <a href="#contact" className="nav-link">Contact</a>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section id="home" className="hero-section">
        <div className="hero-content">
          <div className="hero-text">
            <h1 className="hero-title">
              Strategic Growth,<br />
              <span className="gold-text">Consistent Returns</span>
            </h1>
            <p className="hero-subtitle">
              Teranga Capital Investments delivers exceptional returns through sophisticated trading strategies in U.S. market indices and metal commodities.
            </p>
            <div className="hero-stats">
              <div className="stat-item">
                <div className="stat-value">120%</div>
                <div className="stat-label">Annual Returns Since 2021</div>
              </div>
            </div>
            <a href="#contact" className="btn-primary">
              Contact Us
              <ArrowRight size={20} />
            </a>
          </div>
        </div>
      </section>

      {/* Performance Section */}
      <section id="performance" className="performance-section">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Proven Track Record</h2>
            <p className="section-subtitle">
              Our systematic approach has delivered industry-leading returns consistently since 2021
            </p>
          </div>
          <div className="performance-grid">
            <div className="performance-card">
              <div className="performance-number gold-text">120%</div>
              <div className="performance-label">Average Annual Returns</div>
              <div className="performance-description">Consistent year-over-year growth since 2021</div>
            </div>
            <div className="performance-card">
              <div className="performance-number gold-text">30%</div>
              <div className="performance-label">Average Quarterly Returns</div>
              <div className="performance-description">Steady quarterly performance across market cycles</div>
            </div>
            <div className="performance-card">
              <div className="performance-number gold-text">10%</div>
              <div className="performance-label">Average Monthly Returns</div>
              <div className="performance-description">Regular monthly income generation</div>
            </div>
          </div>
        </div>
      </section>

      {/* Investment Focus Section */}
      <section id="focus" className="focus-section">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Investment Focus</h2>
            <p className="section-subtitle">
              We specialize in high-growth markets with proven trading strategies
            </p>
          </div>
          <div className="focus-grid">
            <div className="focus-card">
              <div className="focus-icon">
                <TrendingUp size={40} />
              </div>
              <h3 className="focus-title">Metal Commodities</h3>
              <p className="focus-description">
                Core focus on precious metals including gold, silver, platinum, and palladium. Our expertise in metals markets provides consistent returns through strategic positioning and deep market analysis of supply-demand fundamentals.
              </p>
            </div>
            <div className="focus-card">
              <div className="focus-icon">
                <Target size={40} />
              </div>
              <h3 className="focus-title">U.S. Market Indices</h3>
              <p className="focus-description">
                Systematic trading focused on U.S. equity indices including S&P 500, NASDAQ 100, and Dow Jones Industrial Average. Our data-driven approach captures market movements across American economic cycles.
              </p>
            </div>
            <div className="focus-card">
              <div className="focus-icon">
                <Shield size={40} />
              </div>
              <h3 className="focus-title">Risk Management</h3>
              <p className="focus-description">
                Disciplined position sizing, diversification across U.S. equities and metals, and sophisticated hedging strategies protect capital while maximizing returns through all market conditions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="about-section">
        <div className="section-container">
          <div className="about-content">
            <div className="about-text">
              <h2 className="section-title">About Teranga Capital</h2>
              <p className="about-paragraph">
                Teranga Capital Investments is a premier investment management firm specializing in alternative asset trading. Since 2021, we have consistently delivered exceptional returns to our investors through disciplined risk management and sophisticated trading strategies.
              </p>
              <p className="about-paragraph">
                Our approach combines quantitative analysis, fundamental research, and real-time market monitoring to identify high-probability opportunities across U.S. equity indices and metal commodities markets.
              </p>
              <p className="about-paragraph">
                We are committed to transparency, consistent performance, and building long-term relationships with our investors based on trust and results.
              </p>
            </div>
            <div className="about-features">
              <div className="feature-item">
                <div className="feature-icon gold-text">✓</div>
                <div className="feature-text">
                  <h4>Disciplined Risk Management</h4>
                  <p>Sophisticated strategies to protect capital while maximizing returns</p>
                </div>
              </div>
              <div className="feature-item">
                <div className="feature-icon gold-text">✓</div>
                <div className="feature-text">
                  <h4>Transparent Reporting</h4>
                  <p>Regular performance updates and comprehensive portfolio insights</p>
                </div>
              </div>
              <div className="feature-item">
                <div className="feature-icon gold-text">✓</div>
                <div className="feature-text">
                  <h4>Proven Track Record</h4>
                  <p>120% average annual returns since 2021</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact-section">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Get In Touch</h2>
            <p className="section-subtitle">
              Ready to grow your wealth? Contact us to learn more about investment opportunities
            </p>
          </div>
          <div className="contact-content">
            <div className="contact-info">
              <div className="contact-item">
                <div className="contact-icon">
                  <Mail size={24} />
                </div>
                <div className="contact-details">
                  <div className="contact-label">Email</div>
                  <a href="mailto:info@teranga.capital" className="contact-value">info@teranga.capital</a>
                </div>
              </div>
              <div className="contact-description">
                <p>
                  We welcome inquiries from sophisticated investors seeking consistent returns through professional asset management. Our team will respond to your inquiry within 24 hours.
                </p>
              </div>
            </div>
            <div className="contact-form-container">
              {formSubmitted ? (
                <div className="form-success">
                  <div className="success-icon">✓</div>
                  <h3>Message Sent Successfully</h3>
                  <p>We'll get back to you within 24 hours.</p>
                </div>
              ) : (
                <form className="contact-form" onSubmit={handleSubmit}>
                  <div className="form-group">
                    <label htmlFor="name" className="form-label">Full Name</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="form-input"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="email" className="form-label">Email Address</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="form-input"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="phone" className="form-label">Phone Number</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="form-input"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="message" className="form-label">Message</label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      className="form-textarea"
                      rows="5"
                      required
                    ></textarea>
                  </div>
                  {errorMessage && (
                    <div className="form-error">
                      <p>{errorMessage}</p>
                    </div>
                  )}
                  <button type="submit" className="btn-primary btn-full" disabled={isSubmitting}>
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                    <ArrowRight size={20} />
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-logo">
            <img 
              src="https://customer-assets.emergentagent.com/job_146d9f7f-91a9-44b9-b93f-54116f665c02/artifacts/l80688sx_337F2E5A-C7A4-47BA-A13A-887FE2B999C7.png" 
              alt="Teranga Capital Investments" 
              className="footer-logo-img"
            />
          </div>
          <div className="footer-info">
            <p className="footer-text">
              Teranga Capital Investments - Professional asset management delivering consistent returns since 2021.
            </p>
            <div className="footer-contact">
              <a href="mailto:info@teranga.capital" className="footer-link">info@teranga.capital</a>
            </div>
          </div>
          <div className="footer-bottom">
            <p className="footer-copyright">
              © {new Date().getFullYear()} Teranga Capital Investments. All rights reserved.
            </p>
            <p className="footer-disclaimer">
              Investment involves risk. Past performance does not guarantee future results.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;